import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Trophy, Crown, Medal, History, Coins } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function prettyMonth(key) {
  if (!key) return "—";
  const [y, m] = key.split("-").map((s) => parseInt(s, 10));
  if (!y || !m) return key;
  return `${MONTH_LABELS[m - 1]} ${y}`;
}

/**
 * ReferralLeaderboard — top 10 referrers with a month / all-time toggle and
 * a Hall of Fame strip listing past monthly winners. Highlights the user's
 * own row and surfaces the +200 monthly bonus the contest pays out.
 */
export function ReferralLeaderboard({ token }) {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [hof, setHof] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API_URL}/api/tokens/referral/leaderboard?period=${period}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/api/tokens/referral/hall-of-fame`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setData(a.data);
        setHof(b.data?.winners || []);
      } catch (e) {
        console.error("Leaderboard load failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, period]);

  const rows = data?.leaderboard || [];
  const you = data?.you || {};

  return (
    <Card className="bg-white/85 border-ink-200" data-testid="referral-leaderboard">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg text-ink-950 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-coral-500" />
            Top Referrers
          </CardTitle>
          {/* period toggle */}
          <div className="inline-flex p-1 rounded-full bg-ink-100" role="tablist">
            {[
              { key: "month",    label: "This month" },
              { key: "all_time", label: "All-time" },
            ].map((opt) => (
              <button
                key={opt.key}
                role="tab"
                aria-selected={period === opt.key}
                onClick={() => setPeriod(opt.key)}
                data-testid={`leaderboard-tab-${opt.key}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                  period === opt.key
                    ? "bg-ink-950 text-white shadow-[0_4px_12px_rgba(10,10,10,0.18)]"
                    : "text-ink-500 hover:text-ink-950"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contest tagline */}
        {period === "month" && (
          <p className="text-xs text-ink-500 mt-2 font-light">
            <Coins className="inline h-3 w-3 mr-1 text-coral-500" />
            Every 1st of the month, the #1 referrer earns <strong className="text-ink-950">+200 bonus tokens</strong>.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="space-y-2 animate-pulse">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 rounded-xl bg-ink-100" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink-200 p-8 text-center">
            <Trophy className="h-10 w-10 mx-auto mb-3 text-ink-300" />
            <p className="font-semibold text-ink-950">
              {period === "month" ? "Nobody has referred yet this month" : "No referrals yet"}
            </p>
            <p className="text-sm text-ink-500 mt-1">
              {period === "month"
                ? "Be the first to refer this month — winner takes the 200-token crown."
                : "Be the first on the leaderboard — share your link and watch your name climb."}
            </p>
          </div>
        ) : (
          rows.map((row, i) => (
            <motion.div
              key={`${row.rank}-${row.name}-${period}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className={`flex items-center gap-3 p-3 rounded-2xl border transition-colors ${
                row.is_you
                  ? "bg-ink-950 text-white border-ink-950 shadow-coral"
                  : "bg-white border-ink-200/70 hover:border-ink-300"
              }`}
              data-testid={`leaderboard-row-${row.rank}`}
            >
              <RankBadge rank={row.rank} highlighted={row.is_you} />
              <div className="flex-1 min-w-0">
                <p className={`font-semibold truncate ${row.is_you ? "text-white" : "text-ink-950"}`}>
                  {row.name}
                  {row.is_you && (
                    <span className="ml-2 inline-block rounded-full bg-citrus-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-950">
                      You
                    </span>
                  )}
                </p>
                <p className={`text-xs ${row.is_you ? "text-white/70" : "text-ink-500"}`}>
                  +{row.tokens_earned} tokens earned · all-time
                </p>
              </div>
              <div className="text-right">
                <p className={`font-display font-black text-2xl tracking-[-0.03em] ${
                  row.is_you ? "text-citrus-500" : "text-ink-950"
                }`}>
                  {row.referrals_count}
                </p>
                <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${
                  row.is_you ? "text-white/60" : "text-ink-500"
                }`}>
                  {period === "month" ? "this month" : "friends"}
                </p>
              </div>
            </motion.div>
          ))
        )}

        {!loading && !you.in_top_10 && you.rank && (
          <div
            className="mt-3 pt-3 border-t border-dashed border-ink-200"
            data-testid="leaderboard-your-rank"
          >
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-coral-500/10 border border-coral-500/30">
              <RankBadge rank={you.rank} highlighted />
              <div className="flex-1">
                <p className="font-semibold text-ink-950">
                  You're #{you.rank}
                  <span className="ml-2 text-xs font-normal text-ink-500">
                    keep going — top 10 is within reach.
                  </span>
                </p>
                <p className="text-xs text-ink-500">+{you.tokens_earned} tokens earned</p>
              </div>
              <p className="font-display font-black text-2xl text-coral-500">
                {you.referrals_count}
              </p>
            </div>
          </div>
        )}

        {/* Hall of Fame strip */}
        {hof.length > 0 && (
          <div className="mt-6 pt-5 border-t border-ink-200" data-testid="hall-of-fame">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-coral-500" />
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink-500">
                Hall of Fame
              </p>
            </div>
            <div className="space-y-1.5">
              {hof.slice(0, 4).map((w, i) => (
                <div
                  key={`${w.month}-${i}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Crown className="h-4 w-4 text-citrus-500 shrink-0" />
                    <span className="font-semibold text-ink-950 truncate">
                      {w.winner_name || "—"}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                      {prettyMonth(w.month)}
                    </span>
                  </div>
                  <span className="text-xs text-ink-500 shrink-0">
                    <strong className="text-ink-950">{w.referrals_count}</strong>{" "}
                    friends · +{w.tokens_awarded} tokens
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RankBadge({ rank, highlighted }) {
  const Icon = rank === 1 ? Crown : rank <= 3 ? Medal : null;
  let tone = "bg-ink-100 text-ink-700";
  if (rank === 1) tone = "bg-citrus-500 text-ink-950";
  else if (rank === 2) tone = "bg-coral-500 text-white";
  else if (rank === 3) tone = "bg-ink-700 text-white";
  if (highlighted && rank > 3) tone = "bg-white text-ink-950";

  return (
    <span
      className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full font-display font-black text-base ${tone}`}
    >
      {Icon ? <Icon className="h-4 w-4" /> : `#${rank}`}
    </span>
  );
}

export default ReferralLeaderboard;
